import { Dispatch, SetStateAction, useCallback } from 'react';
import { ReviewsExportProgress } from '@/context/reviews-page.context';
import { resolveOperationId } from '@/shared/operation-id';

type UseExportLifecycleArgs = {
    isSocketConnected: boolean;
    setExportProgress: Dispatch<SetStateAction<ReviewsExportProgress | null>>;
    scheduleExportProgressCleanup: (exportOperationId: string) => void;
};

type UseExportLifecycleReturn = {
    startExport: () => string;
    finishExportSuccess: (exportOperationId: string, limitWarning?: string | null) => void;
    finishExportFailure: (exportOperationId: string) => void;
    finishExportCanceled: (exportOperationId: string) => void;
};

export const useExportLifecycle = ({
    isSocketConnected,
    setExportProgress,
    scheduleExportProgressCleanup,
}: UseExportLifecycleArgs): UseExportLifecycleReturn => {
    const startExport = useCallback(() => {
        const exportOperationId = resolveOperationId(undefined, Date.now());
        const startedAt = Date.now();

        setExportProgress({
            isRunning: true,
            startedAt,
            exportOperationId,
            processedRows: 0,
            totalRows: null,
            etaSeconds: null,
            isSocketConnected,
            isProgressAvailable: isSocketConnected,
            limitWarning: null,
        });

        return exportOperationId;
    }, [isSocketConnected, setExportProgress]);

    const finishExportSuccess = useCallback(
        (exportOperationId: string, limitWarning?: string | null) => {
            setExportProgress((current) => {
                if (!current || current.exportOperationId !== exportOperationId) {
                    return current;
                }

                return {
                    ...current,
                    isRunning: false,
                    processedRows: current.totalRows !== null ? current.totalRows : current.processedRows,
                    etaSeconds: 0,
                    limitWarning: limitWarning ?? current.limitWarning,
                };
            });
            scheduleExportProgressCleanup(exportOperationId);
        },
        [scheduleExportProgressCleanup, setExportProgress],
    );

    const finishExportFailure = useCallback(
        (exportOperationId: string) => {
            setExportProgress((current) => {
                if (!current || current.exportOperationId !== exportOperationId) {
                    return current;
                }

                return {
                    ...current,
                    isRunning: false,
                };
            });
            scheduleExportProgressCleanup(exportOperationId);
        },
        [scheduleExportProgressCleanup, setExportProgress],
    );

    const finishExportCanceled = useCallback(
        (exportOperationId: string) => {
            setExportProgress((current) => {
                if (!current || current.exportOperationId !== exportOperationId) {
                    return current;
                }

                return {
                    ...current,
                    isRunning: false,
                    etaSeconds: 0,
                };
            });
            scheduleExportProgressCleanup(exportOperationId);
        },
        [scheduleExportProgressCleanup, setExportProgress],
    );

    return {
        startExport,
        finishExportSuccess,
        finishExportFailure,
        finishExportCanceled,
    };
};
